/**
 * ContextPack adoption observability (#151).
 *
 * Session-scoped cache of ContextPack / analyzeImpact metadata for emit-time
 * adoption marking on subsequent Read/Glob/Grep tool_exec spans.
 */

import { generateId } from "@aaac/observability";
import type { CanonicalLink } from "@aaac/observability";
import type { ContextPack } from "./analyzer-bridge.js";

export type AdoptionKind = "pack_hit" | "pack_redundant" | "pack_miss";

export interface AdoptionMarking {
  kind: AdoptionKind;
  contextPackId: string;
  links: CanonicalLink[];
}

interface PackRecord {
  contextPackId: string;
  spanId: string;
  paths: Set<string>;
  readPaths: Set<string>;
}

const PATH_PROBE_TOOLS = new Set(["Read", "Grep", "Glob"]);
const CONTEXT_PACK_TOOLS = new Set([
  "analyzer.generateContextPack",
  "generateContextPack",
]);
const CONTEXT_MAP_TOOLS = new Set([
  "analyzer.getContextMap",
  "getContextMap",
]);
const IMPACT_TOOLS = new Set([
  "analyzer.analyzeImpact",
  "analyzeImpact",
]);

const MAX_ENTRIES_PATHS = 20;
const MAX_TOP_PATHS = 20;

/** Normalise file paths for set membership checks. */
export function normalizeToolPath(path: string): string {
  let p = path.trim().replace(/\\/g, "/");
  if (p.startsWith("./")) p = p.slice(2);
  while (p.startsWith("/")) p = p.slice(1);
  return p.toLowerCase();
}

/** Extract probe paths from Read/Glob/Grep tool input. */
export function extractProbePaths(toolName: string, input: unknown): string[] {
  if (input == null || typeof input !== "object") return [];
  const obj = input as Record<string, unknown>;
  const paths: string[] = [];

  if (toolName === "Read") {
    const fp = obj.file_path ?? obj.path;
    if (typeof fp === "string" && fp.length > 0) paths.push(fp);
    return paths;
  }

  if (toolName === "Grep") {
    const fp = obj.path ?? obj.file_path;
    if (typeof fp === "string" && fp.length > 0) paths.push(fp);
    return paths;
  }

  if (toolName === "Glob") {
    const fp = obj.path ?? obj.cwd;
    if (typeof fp === "string" && fp.length > 0) paths.push(fp);
    return paths;
  }

  return paths;
}

export function isPathProbeTool(toolName: string): boolean {
  return PATH_PROBE_TOOLS.has(toolName);
}

export function isContextPackTool(toolName: string): boolean {
  return CONTEXT_PACK_TOOLS.has(toolName);
}

export function isContextMapTool(toolName: string): boolean {
  return CONTEXT_MAP_TOOLS.has(toolName);
}

export function isImpactTool(toolName: string): boolean {
  return IMPACT_TOOLS.has(toolName);
}

export function isAnalyzerObservabilityTool(toolName: string): boolean {
  return isContextPackTool(toolName) || isContextMapTool(toolName) || isImpactTool(toolName);
}

export function buildContextPackOutputSummary(pack: ContextPack): string {
  const entriesPaths = pack.entries
    .map((e) => e.path)
    .slice(0, MAX_ENTRIES_PATHS);
  return JSON.stringify({
    files_included: pack.metadata.filesIncluded,
    files_referenced: pack.metadata.filesReferenced,
    artifacts_covered: pack.metadata.artifactsCovered,
    total_tokens: pack.metadata.totalTokens,
    budget_tokens: pack.metadata.budgetTokens,
    entries_paths: entriesPaths,
  });
}

export function buildContextMapOutputSummary(map: Record<string, unknown>): string {
  const readTargets = Array.isArray(map.suggestedReadTargets)
    ? (map.suggestedReadTargets as Array<{ path?: string }>)
    : [];
  const paths = readTargets
    .map((t) => t.path)
    .filter((p): p is string => typeof p === "string")
    .slice(0, MAX_TOP_PATHS);
  return JSON.stringify({
    roots: Array.isArray(map.roots) ? map.roots.length : 0,
    nodes: Array.isArray(map.nodes) ? map.nodes.length : 0,
    edges: Array.isArray(map.edges) ? map.edges.length : 0,
    read_targets: readTargets.length,
    top_paths: paths,
  });
}

export function buildAnalyzeImpactOutputSummary(report: Record<string, unknown>): string {
  const directly = Array.isArray(report.directlyAffected)
    ? (report.directlyAffected as Array<{ path?: string }>)
    : [];
  const transitive = Array.isArray(report.transitivelyAffected)
    ? (report.transitivelyAffected as Array<{ path?: string }>)
    : [];
  const allPaths = [...directly, ...transitive]
    .map((e) => e.path)
    .filter((p): p is string => typeof p === "string")
    .slice(0, MAX_TOP_PATHS);
  const maxDepth = Math.max(
    ...directly.map((e) => (e as { depth?: number }).depth ?? 0),
    ...transitive.map((e) => (e as { depth?: number }).depth ?? 0),
    0,
  );
  return JSON.stringify({
    affected_count: directly.length + transitive.length,
    max_depth_reached: maxDepth,
    top_paths: allPaths,
  });
}

/** Build close attributes + register paths from an analyzer MCP handler result. */
export function buildAnalyzerCloseAttributes(
  toolName: string,
  resultText: string,
): { extraAttributes: Record<string, string>; paths: string[]; contextPackId?: string } {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(resultText) as Record<string, unknown>;
  } catch {
    return { extraAttributes: {}, paths: [] };
  }

  if (isContextPackTool(toolName)) {
    const pack = parsed as unknown as ContextPack;
    const contextPackId = generateId();
    const outputSummary = buildContextPackOutputSummary(pack);
    const paths = (pack.entries ?? []).map((e) => e.path);
    return {
      extraAttributes: {
        "tool.output_summary": outputSummary,
        "tool.context_pack_id": contextPackId,
      },
      paths,
      contextPackId,
    };
  }

  if (isContextMapTool(toolName)) {
    const contextPackId = generateId();
    const outputSummary = buildContextMapOutputSummary(parsed);
    const readTargets = Array.isArray(parsed.suggestedReadTargets)
      ? (parsed.suggestedReadTargets as Array<{ path?: string; symbolId?: string }>)
      : [];
    const paths = readTargets
      .map((t) => t.path ?? t.symbolId)
      .filter((p): p is string => typeof p === "string");
    return {
      extraAttributes: {
        "tool.output_summary": outputSummary,
        "tool.context_pack_id": contextPackId,
        "tool.context_map": "true",
      },
      paths,
      contextPackId,
    };
  }

  if (isImpactTool(toolName)) {
    const outputSummary = buildAnalyzeImpactOutputSummary(parsed);
    const contextPackId = generateId();
    const directly = Array.isArray(parsed.directlyAffected)
      ? (parsed.directlyAffected as Array<{ path?: string }>)
      : [];
    const transitive = Array.isArray(parsed.transitivelyAffected)
      ? (parsed.transitivelyAffected as Array<{ path?: string }>)
      : [];
    const paths = [...directly, ...transitive]
      .map((e) => e.path)
      .filter((p): p is string => typeof p === "string");
    return {
      extraAttributes: {
        "tool.output_summary": outputSummary,
        "tool.context_pack_id": contextPackId,
      },
      paths,
      contextPackId,
    };
  }

  return { extraAttributes: {}, paths: [] };
}

export class ContextPackAdoptionTracker {
  private readonly packsBySession = new Map<string, PackRecord[]>();

  registerPack(
    sessionId: string | undefined,
    spanId: string,
    contextPackId: string,
    paths: string[],
  ): void {
    if (!sessionId) return;
    const normalized = new Set(
      paths.map(normalizeToolPath).filter((p) => p.length > 0),
    );
    const records = this.packsBySession.get(sessionId) ?? [];
    records.push({
      contextPackId,
      spanId,
      paths: normalized,
      readPaths: new Set(),
    });
    this.packsBySession.set(sessionId, records);
  }

  /**
   * Evaluate adoption for a path-probe tool at emit time.
   * Returns undefined when no pack was registered before this call.
   */
  evaluate(
    sessionId: string | undefined,
    toolName: string,
    input: unknown,
  ): AdoptionMarking | undefined {
    if (!sessionId || !isPathProbeTool(toolName)) return undefined;

    const packs = this.packsBySession.get(sessionId);
    if (!packs || packs.length === 0) return undefined;

    const probePaths = extractProbePaths(toolName, input).map(normalizeToolPath);
    if (probePaths.length === 0) return undefined;

    const latestPack = packs[packs.length - 1];
    const probePath = probePaths[0];

    const inPack = latestPack.paths.has(probePath);
    let kind: AdoptionKind;
    if (inPack) {
      kind = latestPack.readPaths.has(probePath) ? "pack_redundant" : "pack_hit";
      latestPack.readPaths.add(probePath);
    } else {
      kind = "pack_miss";
    }

    return {
      kind,
      contextPackId: latestPack.contextPackId,
      links: [
        {
          linkType: "uses_context_from",
          targetSpanId: latestPack.spanId,
        },
      ],
    };
  }
}
